/* Live login service.
 *
 * Takes the credentials the user typed, calls the real API, and builds the
 * role record the sidebar uses from the modules the SERVER reports — not from
 * a client-side table of personas.
 *
 * This file used to hold a role → seeded-email table and the seed password in
 * plain text, and the login page signed in as whichever role was clicked. That
 * shipped a working Super Admin credential to every browser that loaded the
 * bundle. Nothing here knows a password now. */
import { httpTismsApi } from '../../api/httpTismsApi';
import type { LoginResult } from '../../api/httpTismsApi';
import type { Role } from '../../data/tisms-api';
import { pagesForModules } from '../../menu';

export interface AuthLoginOutcome extends LoginResult {
  role: Role;
}

export interface Credentials {
  username: string;
  password: string;
}

export const authLogin = async ({ username, password }: Credentials): Promise<AuthLoginOutcome> => {
  const result = await httpTismsApi.login(username.trim(), password);

  /* What this user may see comes from the SERVER now.
   *
   * It used to be looked up in a client-side table of invented personas, and
   * the lookup was wrong in three places: AUDITOR was given the "MD / CEO"
   * screens, READ_ONLY the "External / NABARD" ones and REVIEWER the "IT
   * Auditor" ones. People were shown a menu belonging to a role they did not
   * hold — some pages they should have had were hidden, others they should not
   * have had were offered and then refused by the API.
   *
   * ROLE_BY_CODE and the persona list it indexed are gone. */
  const code = result.user.role;
  const modules = result.user.modules ?? [];
  const isAdmin = ADMIN_ROLES.includes(code);
  const role: Role = {
    id: code as Role['id'],
    name: result.user.roleName || code,
    scope: result.user.entityScope || 'Not restricted',
    modules: pagesForModules(modules, isAdmin),
  };
  return { ...result, role };
};

/** Roles the API lets into the Administration area (AdminComponent::ADMIN_ROLES). */
const ADMIN_ROLES = ['SUPER_ADMIN', 'INSPECTION_HEAD', 'COMPLIANCE'];
